import React, { useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, LineChart, Plus, Trash2 } from 'lucide-react';
import {
  METRIC_CAPABLE_KINDS,
  PortalFieldKind,
  PortalMetric,
  PortalTaskField,
} from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');

/** نیولاین به‌صورت ثابت نگه داشته شده تا در رشته‌های چندخطی خطای فرار کاراکتر پیش نیاید */
const NEWLINE = String.fromCharCode(10);

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

/**
 * تکست‌اریای «هر خط یک مورد».
 *
 * متنِ در حال تایپ را خودش نگه می‌دارد. اگر مستقیم از روی آرایهٔ خروجی رندر
 * شود، هر فاصله و هر خطِ خالیِ تازه همان لحظه trim/حذف می‌شود و کاربر نه
 * می‌تواند فاصله بزند و نه به خط بعد برود.
 */
const LinesTextarea: React.FC<{
  value: string[];
  onChange: (lines: string[]) => void;
  rows?: number;
  placeholder?: string;
}> = ({ value, onChange, rows = 4, placeholder }) => {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <textarea
      rows={rows}
      value={draft ?? value.join(NEWLINE)}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(
          e.target.value
            .split(NEWLINE)
            .map((x) => x.trim())
            .filter(Boolean),
        );
      }}
      /** با خروج از فیلد، دوباره نسخهٔ تمیزشده نشان داده می‌شود */
      onBlur={() => setDraft(null)}
      placeholder={placeholder}
      className={inputClass}
    />
  );
};

/** برچسب فارسی هر نوع پاسخ، به همان ترتیبی که در سند آمده */
/**
 * فرمت‌هایی که در ساخت تسک قابل انتخاب‌اند.
 *
 * «آزمون» عمداً اینجا نیست: یک آزمون در عمل چند سؤال چندگزینه‌ای است، و همین
 * حالا با افزودن چند فیلد «انتخاب یک گزینه» ساخته می‌شود — با این مزیت که
 * منتور می‌تواند روی تک‌تک سؤال‌ها بازخورد بدهد.
 */
export const FIELD_KIND_LABELS: Partial<Record<PortalFieldKind, string>> = {
  LONG_TEXT: 'پاسخ متنی بلند',
  SHORT_TEXT: 'پاسخ کوتاه',
  NUMBER: 'پاسخ عددی',
  DATE: 'تاریخ یا زمان',
  FILE: 'آپلود فایل',
  IMAGE: 'آپلود تصویر',
  VIDEO: 'آپلود ویدئو',
  AUDIO: 'آپلود فایل صوتی',
  LINK: 'ارسال لینک',
  SINGLE_SELECT: 'انتخاب یک گزینه',
  MULTI_SELECT: 'انتخاب چند گزینه',
  RATING: 'طیف امتیازدهی',
  CHECKLIST: 'چک‌لیست انجام فعالیت',
  TABLE: 'تکمیل جدول',
  CONFIRM: 'تأیید انجام فعالیت',
  PEER_FEEDBACK: 'بازخورد به فرد دیگر',
  TEMPLATE_UPLOAD: 'دانلود قالب و بارگذاری نسخهٔ تکمیل‌شده',
};

/** انواعی که فهرست گزینه یا آیتم می‌خواهند */
const LIST_KINDS: PortalFieldKind[] = ['SINGLE_SELECT', 'MULTI_SELECT', 'CHECKLIST'];
const FILE_KINDS: PortalFieldKind[] = ['FILE', 'IMAGE', 'VIDEO', 'AUDIO', 'TEMPLATE_UPLOAD'];
const TEXT_KINDS: PortalFieldKind[] = ['SHORT_TEXT', 'LONG_TEXT', 'PEER_FEEDBACK'];

export const emptyField = (): PortalTaskField => ({
  kind: 'LONG_TEXT',
  label: '',
  required: true,
  config: {},
});

interface Props {
  fields: PortalTaskField[];
  onChange: (fields: PortalTaskField[]) => void;
  /** شاخص‌های فعال دوره، برای وصل‌کردن فیلدهای عددی */
  metrics?: PortalMetric[];
  /** ساخت شاخص تازه از داخل همین فرم؛ شاخص ساخته‌شده را برمی‌گرداند */
  onCreateMetric?: (title: string) => Promise<PortalMetric | null>;
}

/**
 * ویرایشگر فیلدهای یک تسک.
 *
 * چون تسک صرفاً فهرستی از فیلدهاست، «تسک ترکیبی» چیز جداگانه‌ای نیست:
 * هر تسکی که بیش از یک فیلد داشته باشد ترکیبی است.
 */
export const TaskFieldsEditor: React.FC<Props> = ({
  fields,
  onChange,
  metrics = [],
  onCreateMetric,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [creatingFor, setCreatingFor] = useState<number | null>(null);
  const [newMetricTitle, setNewMetricTitle] = useState('');
  const [metricBusy, setMetricBusy] = useState(false);

  const activeMetrics = metrics.filter((m) => m.isActive);

  const patch = (index: number, next: Partial<PortalTaskField>) =>
    onChange(fields.map((f, i) => (i === index ? { ...f, ...next } : f)));

  /**
   * تغییر نوع فیلد، شاخص را هم پاک می‌کند.
   * بدون این، تبدیل «پاسخ عددی» به «پاسخ کوتاه» یک metricId جامانده می‌گذارد
   * که سرور موقع ذخیره ردش می‌کند و کاربر دلیلش را نمی‌فهمد.
   */
  const changeKind = (index: number, kind: PortalFieldKind) =>
    patch(index, {
      kind,
      config: {},
      ...(METRIC_CAPABLE_KINDS.includes(kind) ? {} : { metricId: null }),
    });

  const submitNewMetric = async (index: number) => {
    const title = newMetricTitle.trim();
    if (!title || !onCreateMetric) return;
    setMetricBusy(true);
    try {
      const created = await onCreateMetric(title);
      if (created) patch(index, { metricId: created.id });
      setCreatingFor(null);
      setNewMetricTitle('');
    } finally {
      setMetricBusy(false);
    }
  };

  const patchConfig = (index: number, next: Record<string, any>) =>
    patch(index, { config: { ...(fields[index].config ?? {}), ...next } });

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
    setOpenIndex(target);
  };

  const remove = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
    setOpenIndex(null);
  };

  return (
    <div className="space-y-2">
      {fields.map((field, index) => {
        const open = openIndex === index;
        const config = field.config ?? {};
        return (
          <div key={index} className="rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 p-3">
              <GripVertical className="h-4 w-4 shrink-0 text-gray-300" />
              <button
                type="button"
                onClick={() => setOpenIndex(open ? null : index)}
                className="flex min-w-0 flex-1 items-center gap-2 text-right"
              >
                {open ? (
                  <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800">
                  {field.label || 'فیلد بدون عنوان'}
                </span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-bold text-gray-600">
                  {FIELD_KIND_LABELS[field.kind] ?? field.kind}
                </span>
                {field.required ? (
                  <span className="shrink-0 text-[10.5px] font-black text-red-500">اجباری</span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => move(index, -1)}
                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100"
                aria-label="بالا"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100"
                aria-label="پایین"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => remove(index)}
                className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50"
                aria-label="حذف"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {open ? (
              <div className="space-y-3 border-t border-gray-100 p-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">نوع پاسخ</span>
                    <select
                      value={field.kind}
                      onChange={(e) => changeKind(index, e.target.value as PortalFieldKind)}
                      className={inputClass}
                    >
                      {(Object.keys(FIELD_KIND_LABELS) as PortalFieldKind[]).map((kind) => (
                        <option key={kind} value={kind}>
                          {FIELD_KIND_LABELS[kind]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">
                      عنوان فیلد (سؤال)
                    </span>
                    <input
                      value={field.label}
                      onChange={(e) => patch(index, { label: e.target.value })}
                      className={inputClass}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-gray-600">
                    راهنمای کوتاه (اختیاری)
                  </span>
                  <input
                    value={field.helpText ?? ''}
                    onChange={(e) => patch(index, { helpText: e.target.value })}
                    className={inputClass}
                  />
                </label>

                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => patch(index, { required: e.target.checked })}
                  />
                  پاسخ به این فیلد اجباری است
                </label>

                {METRIC_CAPABLE_KINDS.includes(field.kind) ? (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                    <div className="mb-2 flex items-center gap-1.5">
                      <LineChart className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
                      <span className="text-xs font-bold text-indigo-800">
                        این فیلد کدام شاخص را می‌سنجد؟
                      </span>
                    </div>

                    {creatingFor === index ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          autoFocus
                          value={newMetricTitle}
                          onChange={(e) => setNewMetricTitle(e.target.value)}
                          placeholder="مثلاً فروش هفتگی"
                          className={inputClass + ' flex-1'}
                        />
                        <button
                          type="button"
                          disabled={metricBusy || !newMetricTitle.trim()}
                          onClick={() => submitNewMetric(index)}
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                        >
                          {metricBusy ? 'در حال ساخت…' : 'بساز و وصل کن'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCreatingFor(null);
                            setNewMetricTitle('');
                          }}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-bold text-gray-600"
                        >
                          انصراف
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={field.metricId ?? ''}
                          onChange={(e) => patch(index, { metricId: e.target.value || null })}
                          className={inputClass + ' flex-1'}
                        >
                          <option value="">هیچ‌کدام — فقط ثبت شود</option>
                          {activeMetrics.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.title}
                              {m.unit ? ' (' + m.unit + ')' : ''}
                            </option>
                          ))}
                        </select>
                        {onCreateMetric ? (
                          <button
                            type="button"
                            onClick={() => setCreatingFor(index)}
                            className="shrink-0 rounded-lg border border-indigo-300 px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
                          >
                            + شاخص جدید
                          </button>
                        ) : null}
                      </div>
                    )}

                    <p className="mt-2 text-[11px] leading-5 text-indigo-700/80">
                      با وصل‌کردن به شاخص، این عدد در نمودار روند دوره می‌نشیند و در طول
                      هفته‌ها قابل مقایسه می‌شود. اگر خالی بماند، عدد فقط ثبت می‌شود.
                    </p>
                  </div>
                ) : null}

                {TEXT_KINDS.includes(field.kind) ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        حداقل کاراکتر
                      </span>
                      <input
                        type="number"
                        value={config.minChars ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            minChars: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        حداکثر کاراکتر
                      </span>
                      <input
                        type="number"
                        value={config.maxChars ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            maxChars: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                  </div>
                ) : null}

                {field.kind === 'NUMBER' ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">کمینه</span>
                      <input
                        type="number"
                        value={config.min ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            min: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">بیشینه</span>
                      <input
                        type="number"
                        value={config.max ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            max: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                  </div>
                ) : null}

                {field.kind === 'RATING' ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">از</span>
                      <input
                        type="number"
                        value={config.scaleMin ?? 1}
                        onChange={(e) => patchConfig(index, { scaleMin: Number(e.target.value) })}
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">تا</span>
                      <input
                        type="number"
                        value={config.scaleMax ?? 10}
                        onChange={(e) => patchConfig(index, { scaleMax: Number(e.target.value) })}
                        className={inputClass}
                      />
                    </label>
                  </div>
                ) : null}

                {LIST_KINDS.includes(field.kind) ? (
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">
                      {field.kind === 'CHECKLIST' ? 'موارد چک‌لیست' : 'گزینه‌ها'} — هر خط یک مورد
                    </span>
                    <LinesTextarea
                      key={index + ':' + field.kind}
                      value={field.kind === 'CHECKLIST' ? (config.items ?? []) : (config.options ?? [])}
                      onChange={(list) =>
                        patchConfig(
                          index,
                          field.kind === 'CHECKLIST' ? { items: list } : { options: list },
                        )
                      }
                    />
                  </label>
                ) : null}

                {field.kind === 'CHECKLIST' ? (
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.requireAll}
                      onChange={(e) => patchConfig(index, { requireAll: e.target.checked })}
                    />
                    همهٔ موارد باید تیک بخورند
                  </label>
                ) : null}

                {field.kind === 'MULTI_SELECT' ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        حداقل انتخاب
                      </span>
                      <input
                        type="number"
                        value={config.minSelected ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            minSelected: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        حداکثر انتخاب
                      </span>
                      <input
                        type="number"
                        value={config.maxSelected ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            maxSelected: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                  </div>
                ) : null}

                {field.kind === 'TABLE' ? (
                  <div className="space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        ستون‌های جدول — هر خط یک ستون
                      </span>
                      <LinesTextarea
                        key={index + ':columns'}
                        value={config.columns ?? []}
                        onChange={(columns) => patchConfig(index, { columns })}
                        placeholder={['اقدام', 'مسئول', 'موعد', 'شاخص موفقیت'].join(NEWLINE)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        حداقل تعداد ردیف
                      </span>
                      <input
                        type="number"
                        value={config.minRows ?? ''}
                        onChange={(e) =>
                          patchConfig(index, {
                            minRows: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className={inputClass}
                      />
                    </label>
                  </div>
                ) : null}

                {FILE_KINDS.includes(field.kind) ? (
                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          حداکثر حجم هر فایل (مگابایت)
                        </span>
                        <input
                          type="number"
                          value={config.maxSizeMb ?? ''}
                          placeholder="پیش‌فرض دوره"
                          onChange={(e) =>
                            patchConfig(index, {
                              maxSizeMb: e.target.value ? Number(e.target.value) : undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          فرمت‌های مجاز — با کاما
                        </span>
                        <input
                          value={(config.acceptedFormats ?? []).join(', ')}
                          placeholder="pdf, docx, xlsx"
                          onChange={(e) =>
                            patchConfig(index, {
                              acceptedFormats: e.target.value
                                .split(',')
                                .map((x) => x.trim().replace(/^\./, ''))
                                .filter(Boolean),
                            })
                          }
                          className={inputClass}
                        />
                      </label>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!config.multiple}
                        onChange={(e) => patchConfig(index, { multiple: e.target.checked })}
                      />
                      امکان ارسال چند فایل
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => {
          onChange([...fields, emptyField()]);
          setOpenIndex(fields.length);
        }}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-blue-300 px-4 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-50"
      >
        <Plus className="h-4 w-4" />
        افزودن فیلد پاسخ
      </button>

      {fields.length ? (
        <p className="text-center text-[11px] font-bold text-gray-400">
          {fa(fields.length)} فیلد — تسک با بیش از یک فیلد، تسک ترکیبی است.
        </p>
      ) : null}
    </div>
  );
};
